import { useRouter } from 'next/router'
import { useCallback, useRef } from 'react'
import { v4 as uuid } from 'uuid'

import { useAddActivityAttemptMutation } from '~/redux/apis/activity-api'
import { ActivityCategory, GenerationMethod } from '~/redux/apis/types'
import { useAppSelector } from '~/redux/hooks'
import { selectCurrentRound } from '~/bootcamp/slices/bootcamp-activity.slice'

import { ActivityQuestionType } from '../../types/bootcamp'

export function useBootcampActivityLogging({
  questionType,
  subtopic,
  bootcampKey,
  moduleKey,
  level,
  activityType,
  activityName,
}: {
  questionType?: ActivityQuestionType
  subtopic: string
  bootcampKey: string
  moduleKey: string
  level: string
  activityType: number
  activityName?: string
}): (correct: boolean, questionId?: string) => void {
  const [postActivity] = useAddActivityAttemptMutation()
  const router = useRouter()
  const currentRound = useAppSelector(selectCurrentRound)

  const path = router.asPath
  const pathWithoutQuery = path.split('?')[0]
  const isShowGrade = router.query.showGrade === 'true'
  const variationKeyRef = useRef<string | undefined>(uuid())

  return useCallback(
    (correct?: boolean, questionId?: string) => {
      if (!questionType || typeof correct !== 'boolean') return

      const activityAttempt = {
        correct,
        activityCategory: ActivityCategory.BOOTCAMP,
        generationMethod: GenerationMethod.DYNAMIC,
        path: pathWithoutQuery,
        round: currentRound.toString(),
        variationKey: variationKeyRef.current,
        metadata: {
          type: questionType,
          category: subtopic,
          subject: bootcampKey,
          bootcampNumber: Number(moduleKey),
          pageLevelNumber: Number(level),
          activityType,
          questionId,
          interactiveActivityName: activityName,
        },
        isShowGrade,
      }

      postActivity({ attempts: [activityAttempt] })

      if (correct) {
        variationKeyRef.current = uuid()
      }
    },
    [
      questionType,
      pathWithoutQuery,
      currentRound,
      subtopic,
      bootcampKey,
      moduleKey,
      level,
      activityType,
      activityName,
      isShowGrade,
      postActivity,
    ],
  )
}
